import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, TextField, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from './../../utils/IconHandler';
import { formatDate } from './../../utils/DateUtils';
import TableRefreshButton from '../../components/TableRefreshButton/TableRefreshButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { formatMoney } from '../../utils/currency';

const ClientsTable = ({ 
  clients = [], 
  paginationModel, 
  onPaginationModelChange, 
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  searchTerm, 
  onSearchChange, 
  totalRows, 
  loyaltyCardEnabled,
  onAdd, 
  onRefresh,
  onEdit, 
  onView, 
  onDelete, 
  loading 
}) => {
  const { t } = useTranslation();
  
  dayjs.extend(relativeTime);
  dayjs.locale('es');

  const columns = [
    { field: 'firstName', headerName: t('first_name'), flex: 1 },
    { field: 'lastName', headerName: t('last_name'), flex: 1 },
    {
      field: 'createdAt',
      headerName: t('joined_from'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = dayjs(params.value);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>{formatDate(date)} <span style={{color:"#a5a5a5", paddingLeft: "4px"}}>({date.fromNow()})</span></div>
          </Box>
        );
      }
    },
    { 
      field: 'totalPurchases', 
      headerName: t('total_purchases'), 
      type: 'number', 
      flex: 0.8, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? `${params.toFixed(0)}` : '—', 
    },
    { 
      field: 'totalSpent', 
      headerName: t('total_spent'), 
      type: 'number', 
      flex: 0.8, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? `${formatMoney(params)}` : '—', 
    },
    { 
      field: 'averageTicket', 
      headerName: t('average_ticket'), 
      type: 'number', 
      flex: 0.8, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? `${formatMoney(params)}` : '—', 
    },
    {
      field: 'lastPurchase',
      headerName: t('last_purchase'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = dayjs(params.value);
        if (!date.isValid()) {
          return <Box sx={{ textAlign: 'center', color: '#a5a5a5' }}>—</Box>;
        }
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>
              {formatDate(date)}
              <span style={{ color: "#a5a5a5", paddingLeft: "4px" }}>
                ({date.fromNow()})
              </span>
            </div>
          </Box>
        );
      }
    },
    ...(loyaltyCardEnabled ? [{
      field: 'loyaltyStamps',
      headerName: t('loyalty_stamps') || 'Sellos acumulados',
      type: 'number',
      flex: 0.9,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row?.loyaltyStamps ?? 0,
    }] : []),
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1.2,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <>
          <IconButton onClick={() => onView(params.row)}>{getIcon('eye')}</IconButton>
          <IconButton onClick={() => onEdit(params.row)}>{getIcon('settings')}</IconButton>
          <IconButton onClick={() => onDelete(params.row)}>{getIcon('trash')}</IconButton>
        </>
      )
    }
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <TextField
          label={t('search')}
          placeholder={t('search_by_name_lastname')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ width: 300 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableRefreshButton loading={loading} onClick={onRefresh} />
          <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
            {t('add_client')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1100 }}>
          <DataGrid
            loading={loading}
            rows={clients}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            sortingMode="server"
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            filterMode="server"
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            rowCount={totalRows}
            getRowId={(row) => row.ci}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientsTable;
